import React, { useEffect, useState, useRef } from 'react';
import {
  Tooltip,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TableRowLabel,
  TableFooterPagination,
  TableFooterShowMore,
  useBreakpoint,
  Badge,
} from '@one-thd/sui-atomic-components';
import { Mobile, TextToConfirm, QRCode } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  DEFAULT_ROWS_PER_PAGE,
  MAX_ROWS_PER_PAGE,
  PAGINATION_THRESHOLD_VALUE,
} from '../../../constants/payments-table';
import { CardSortByLabel, cardSortByMap } from '../../../constants/sort';
import { MeatballsMenu } from '../../shared/meatballs-menu/meatballs-menu';
import { getCardImagePath } from '../../../utils/credit-cards';
import { getLastModifiedDate } from '../../../utils/date';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import { usePayments } from '../../../hooks/use-payments';
import { B2BCreditCardsFilterType } from '../../../api/b2b/payments';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import {
  ACTIVE_SAVES_MSG,
  CHANGES_SAVED_MSG,
  DELETED_CARD_MSG,
  DELETED_CITI_CARDS_SUCCESS_MSG1,
  DELETED_CITI_CARDS_SUCCESS_MSG2,
  HD_PASS_SUCCESS_STOP_AUTH_MSG,
  INACTIVE_SAVES_MSG,
} from '../../../constants/response-success-messages';
import {
  DELETED_CITI_CARDS_ERROR_MSG1,
  DELETED_CITI_CARDS_ERROR_MSG2,
  ERR_GENERIC,
  ERR_UNABLE_TO_SAVE,
} from '../../../constants/response-error-messages';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
import { getPaymentModalsVersion, scrollToTop } from '../../../utils/general';
import { InfoPopover } from '../../shared/popover/popover';
import { CardType } from '../../../constants/payments';
import {
  setCardSortAnalytics,
  setTableViewPaginationAnalytics,
} from '../../../utils/payments-analytics';
import { CustomerRole } from '../../../models/common/customer';

interface ITableViewProps {
  triggerGetPaymentCards: (
    pageNumber: number,
    fType: B2BCreditCardsFilterType,
    fValue: string,
    sortValue: CardSortByLabel | null,
    ascendingOrder: boolean | null,
    pageSize?: number,
  ) => void;
  onChangePage: (page: number) => void;
  onActionSuccess: (
    paymentId: string,
    message: string,
    dataTestId?: string,
  ) => void;
  onActionFailure: (
    paymentId: string,
    message: string,
    dataTestId?: string,
  ) => void;
  canShowHDWallet: boolean;
}

export const TableView = ({
  triggerGetPaymentCards,
  onChangePage,
  onActionFailure,
  onActionSuccess,
  canShowHDWallet,
}: ITableViewProps) => {
  const {
    creditCards,
    paginationInfo,
    currentSortByValue,
    currentPageNumber,
    submittedSearchType,
    submittedSearchValue,
    isSearchActive,
  } = usePayments();
  const [isFooterDropdownOpen, setIsFooterDropdownOpen] = useState(false);
  const tableFooterRef = useRef<HTMLDivElement | null>(null);
  const { role } = useCustomerInfo();
  const {
    onSetActive,
    onSetInactive,
    onViewPurchases,
    onEditCreditCard,
    onDeleteCreditCard,
    onDefaultCreditCardChange,
    onCreditCardRemotePurchases,
    onHomeDepotWallet,
    onRemoveHDWallet,
  } = useMeatballMenuActions();

  const sortDirection = currentSortByValue.ascendingOrder ? 'asc' : 'desc';
  const paymentCards = creditCards?.paymentCards;
  const screen = useBreakpoint('2xl');
  const isSmallScreen = screen.lesser;
  const CELL_WIDTH = {
    CARD: 170,
    NICKNAME: 140,
    EXPIRATION: 118,
    STATUS: 109,
    REMOTE_PURCHASES: 116,
    HD_PASS: 40,
    ADDED_BY: 147,
    LAST_EDITED: 104,
    ACTIONS: 80,
  } as const;
  const isRemoveHDPassEnabled = !!useConfigService('ENABLE_HDPASS_MENU_REMOVE');

  const creditCardsCount =
    (isSearchActive
      ? paginationInfo?.totalSearchedCreditCards
      : paginationInfo?.totalCreditCards) || 0;
  const currentPageNumberTable = currentPageNumber - 1;

  const shouldShowPagination = creditCardsCount > DEFAULT_ROWS_PER_PAGE;
  const paginationClasses = [
    `sui-border-solid sui-border-primary sui-border-b-1`,
    'sui-border-x-1 sui-border-primary sui-text-left sui-px-4 sui-py-2',
  ].join(' ');

  const createdBySortDirection =
    currentSortByValue.sortBy === 'Added By' ? sortDirection : null;
  const cardNumberSortDirection =
    currentSortByValue.sortBy === 'Card Number' ? sortDirection : null;
  const expDateSortDirection =
    currentSortByValue.sortBy === 'Expiration Date' ? sortDirection : null;
  const lastEditedSortDirection =
    currentSortByValue.sortBy === 'Last Edited Date' ||
    !currentSortByValue.sortBy
      ? sortDirection
      : null;
  const nicknameSortDirection =
    currentSortByValue.sortBy === 'Nickname' ? sortDirection : null;
  const statusSortDirection =
    currentSortByValue.sortBy === 'Status' ? sortDirection : null;
  const [numberOfRowsPerPage, setNumberOfRowsPerPage] = useState(
    DEFAULT_ROWS_PER_PAGE,
  );

  useEffect(() => {
    // Note: Remove when stencil TableFooterPagination will has a prop for custom dropdown
    const hideViewAllOption = () => {
      if (
        isFooterDropdownOpen &&
        tableFooterRef.current &&
        creditCardsCount > MAX_ROWS_PER_PAGE
      ) {
        const dropdownOptions = tableFooterRef.current.querySelectorAll(
          'ul[role="listbox"] > li',
        );
        const viewAllOption = dropdownOptions[dropdownOptions.length - 1];
        if (
          viewAllOption.textContent &&
          /view all/i.test(viewAllOption.textContent)
        ) {
          viewAllOption.classList.add('sui-hidden');
        }
      }
    };

    hideViewAllOption();
  }, [isFooterDropdownOpen, creditCardsCount, tableFooterRef]);

  const onPageChangeHandler = (_event: Event, newPage: number) => {
    setTableViewPaginationAnalytics({
      rows: numberOfRowsPerPage,
      isSearchActive,
      filterType: submittedSearchType,
      currentPageNumber: newPage + 1,
    });

    onChangePage(newPage + 1);
  };

  const onRowsPerPageChangeHandler = (_event: Event, rows: number) => {
    setTableViewPaginationAnalytics({
      rows,
      isSearchActive,
      filterType: submittedSearchType,
      currentPageNumber: null,
    });
    setNumberOfRowsPerPage(rows);

    triggerGetPaymentCards(
      1,
      submittedSearchType,
      submittedSearchValue,
      currentSortByValue.sortBy,
      currentSortByValue.ascendingOrder,
      rows,
    );
  };

  const sortByField = (field: CardSortByLabel) => {
    const ascOrder = !(
      currentSortByValue.sortBy === field && sortDirection === 'asc'
    );

    triggerGetPaymentCards(
      currentPageNumber,
      submittedSearchType,
      submittedSearchValue,
      field,
      ascOrder,
      currentSortByValue.pageSize,
    );

    setCardSortAnalytics(
      true,
      field,
      cardSortByMap[field || 'Card Type'].orders[ascOrder ? 0 : 1],
    );
  };
  const renderCardTableRow = (card: IPaymentCardCommonData) => {
    const {
      isDefault,
      paymentId,
      cardNumberLast4,
      buyerId,
      cardNickName,
      cardExpiryMonth,
      cardExpiryYear,
      cardStatus,
      cardBrand,
      cardType,
      authorizeFlag,
      t2cStatus,
      isCardExpired,
      addedBy,
      lastModifiedDate,
      hdWalletAuthorized,
    } = card;

    const isAdmin = role === CustomerRole.ADMIN;
    const isCardAddedByPurchaser = card.level === 'user';
    const isHiddenForAdmin = isAdmin && isCardAddedByPurchaser;
    const isExpired = card.isCardExpired;
    const isCardInactive = cardStatus?.toLowerCase() === 'inactive';
    const isT2CAuthorized = t2cStatus?.toLowerCase() === 'enrolled';
    const isPhoneAndT2CAuthorized = card.authorizeFlag && isT2CAuthorized;
    const isHDWalletAuth = hdWalletAuthorized?.toUpperCase() === 'Y';
    const canShowHDWalletIcon = canShowHDWallet && isHDWalletAuth;
    const handleDefaultChange = () => {
      const onSuccess = () => {
        onActionSuccess(
          paymentId,
          CHANGES_SAVED_MSG.message,
          CHANGES_SAVED_MSG.dataTestId,
        );
      };
      const onFailure = () => {
        onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
      };
      onDefaultCreditCardChange(card, onSuccess, onFailure);
    };

    const handleDeleteCard = () => {
      const onSuccess = () => {
        if (card.citiAccountId) {
          const citiSuccessMsg =
            DELETED_CITI_CARDS_SUCCESS_MSG1.message +
            card.citiAccountId +
            DELETED_CITI_CARDS_SUCCESS_MSG2.message;
          onActionSuccess(paymentId, citiSuccessMsg);
        } else {
          onActionSuccess(
            paymentId,
            DELETED_CARD_MSG.message,
            DELETED_CARD_MSG.dataTestId,
          );
        }
      };
      const onFailure = () => {
        if (card.citiAccountId) {
          const citiErrorMsg =
            DELETED_CITI_CARDS_ERROR_MSG1.message +
            card.citiAccountId +
            DELETED_CITI_CARDS_ERROR_MSG2.message;
          onActionFailure(paymentId, citiErrorMsg);
        } else {
          onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
        }
      };
      onDeleteCreditCard(card, onSuccess, onFailure);
    };

    const handleSetActive = () => {
      const onSuccess = () => {
        onActionSuccess(
          paymentId,
          ACTIVE_SAVES_MSG.message,
          ACTIVE_SAVES_MSG.dataTestId,
        );
      };
      const onFailure = () => {
        onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
      };
      onSetActive(card, CardType.CREDIT_CARD, onSuccess, onFailure);
    };

    const handleSetInactive = () => {
      const onSuccess = () => {
        onActionSuccess(
          paymentId,
          INACTIVE_SAVES_MSG.message,
          INACTIVE_SAVES_MSG.dataTestId,
        );
      };
      const onFailure = () => {
        onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
      };
      onSetInactive(card, CardType.CREDIT_CARD, onSuccess, onFailure);
    };
    const handleRemoveHomeDepotWallet = () => {
      const onSuccess = () => {
        onActionSuccess(paymentId, HD_PASS_SUCCESS_STOP_AUTH_MSG.message);
      };
      const onFailure = () => {
        scrollToTop();
        onActionFailure(paymentId, ERR_GENERIC.message);
      };
      onRemoveHDWallet(card, onSuccess, onFailure);
    };
    const b2bMenuItems = [
      {
        value: 'Edit',
        action: () => {
          onEditCreditCard(card);
        },
        isDisabled: !card.actions?.edit,
        isHidden: isHiddenForAdmin,
        dataTestId: 'edit-meatball-action',
      },
      {
        value: 'Set As Default',
        action: handleDefaultChange,
        isHidden: isDefault || isHiddenForAdmin,
        isDisabled: isCardInactive || !card.actions?.setasDefault,
        dataTestId: 'set-default-meatball-action',
      },
      {
        value: 'View Purchases',
        action: () => {
          onViewPurchases(card, CardType.CREDIT_CARD);
        },
        isDisabled: !card.actions?.viewPurchases,
        dataTestId: 'view-purchases-meatball-action',
      },
      {
        value: 'HD Pass',
        action: () => {
          onHomeDepotWallet(card);
        },
        isDisabled: isCardInactive || isExpired,
        isHidden:
          isRemoveHDPassEnabled ||
          !canShowHDWallet ||
          isHDWalletAuth ||
          card.actions?.removeHDWalletAuth ||
          isHiddenForAdmin ||
          !getPaymentModalsVersion(),
        dataTestId: 'hd-pass-meatball-action',
      },
      {
        value: 'Remove HD Pass',
        action: () => {
          handleRemoveHomeDepotWallet();
        },
        isDisabled: isCardInactive || isExpired,
        isHidden:
          !canShowHDWallet ||
          !card.actions?.removeHDWalletAuth ||
          isHiddenForAdmin ||
          !getPaymentModalsVersion(),
        dataTestId: 'remove-hd-pass-meatball-action',
      },
      {
        value: 'Remote Purchases',
        action: () => {
          onCreditCardRemotePurchases(card);
        },
        isDisabled: isCardInactive || isExpired,
        isHidden: isHiddenForAdmin || !isAdmin || !getPaymentModalsVersion(),
        dataTestId: 'remote-purchases-meatball-action',
      },
      {
        value: 'Set Active',
        action: handleSetActive,
        isHidden:
          isHiddenForAdmin || !card.actions?.setActive || !isCardInactive,
        isDisabled: !card.actions?.setActive,
        dataTestId: 'set-active-meatball-action',
      },
      {
        value: 'Set Inactive',
        action: handleSetInactive,
        isHidden:
          isHiddenForAdmin || (isCardInactive && !isPhoneAndT2CAuthorized),
        isDisabled: isPhoneAndT2CAuthorized || !card.actions?.setInactive,
        dataTestId: 'set-inactive-meatball-action',
      },
      {
        value: 'Delete',
        action: handleDeleteCard,
        isHidden: isHiddenForAdmin,
        dataTestId: 'delete-meatball-action',
      },
    ];

    const badgeColor: Record<string, string> = {
      inactive: 'medium',
      active: 'info',
      expired: 'danger',
    };

    return (
      <TableRow
        key={paymentId}
        className={`${isCardInactive ? 'sui-text-inactive' : ''}`}
      >
        <TableCell
          width={CELL_WIDTH.CARD}
          sticky={isSmallScreen ? 'horizontal' : undefined}
        >
          {isDefault && (
            <Typography variant="body-xs" weight="bold" color="subtle">
              Default Payment Method
            </Typography>
          )}
          <img
            width={33}
            height="auto"
            className="sui-inline-block sui-mr-1"
            src={getCardImagePath(cardBrand, cardType)}
            alt="Credit card logo"
          />
          ****{cardNumberLast4} {buyerId}
        </TableCell>
        <TableCell>
          <Typography
            variant="body-base"
            title={cardNickName}
            color={isCardInactive ? 'inactive' : 'primary'}
          >
            <span className="sui-break-words">{cardNickName}</span>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography color={isCardInactive ? 'inactive' : 'primary'}>
            {cardExpiryMonth &&
              cardExpiryYear &&
              `${cardExpiryMonth}/${cardExpiryYear.slice(-2)}`}
          </Typography>
        </TableCell>
        <TableCell>
          <div className="sui-flex sui-flex-col sui-gap-1">
            {isCardExpired ? (
              <Badge
                variant="subtle"
                color={badgeColor.expired}
                style={{
                  borderStyle: 'solid',
                }}
              >
                Expired
              </Badge>
            ) : (
              <Badge
                variant="subtle"
                color={badgeColor[cardStatus?.toLowerCase() || 'active']}
                style={{
                  borderStyle: 'solid',
                }}
              >
                {cardStatus || 'Active'}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          {(authorizeFlag || isT2CAuthorized) && (
            <div className="sui-flex sui-gap-2">
              {authorizeFlag && (
                <Tooltip
                  offset="relaxed"
                  placement="bottom-start"
                  title="Card authorized to approve purchases by phone"
                >
                  <Mobile />
                </Tooltip>
              )}
              {isT2CAuthorized && (
                <Tooltip
                  offset="relaxed"
                  placement="bottom-start"
                  title="Card authorized to approve purchases by text"
                >
                  <TextToConfirm />
                </Tooltip>
              )}
            </div>
          )}
        </TableCell>
        {canShowHDWallet && (
          <TableCell padding="none" align="left">
            {canShowHDWalletIcon && (
              <Tooltip
                offset="relaxed"
                placement="bottom-start"
                title="Card authorized to make purchases with HD Pass"
              >
                <div
                  className="sui-flex sui-gap-2"
                  data-testid="hd-pass-auth-icon"
                >
                  <QRCode color={isCardInactive ? 'subtle' : 'primary'} />
                </div>
              </Tooltip>
            )}
          </TableCell>
        )}
        <TableCell>
          <Typography color={isCardInactive ? 'inactive' : 'primary'}>
            {addedBy}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography color={isCardInactive ? 'inactive' : 'primary'}>
            {lastModifiedDate ? getLastModifiedDate(lastModifiedDate) : ''}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <MeatballsMenu
            label="Payment"
            iconSize="regular"
            MenuProps={{
              PaperProps: {
                className: 'sui-z-20',
              },
            }}
            items={b2bMenuItems}
          />
        </TableCell>
      </TableRow>
    );
  };

  const tablePagination =
    creditCardsCount > PAGINATION_THRESHOLD_VALUE ? (
      <TableFooterPagination
        data-testid="TableFooterPagination"
        page={currentPageNumberTable}
        count={creditCardsCount}
        rowsPerPage={DEFAULT_ROWS_PER_PAGE}
        onPageChange={onPageChangeHandler}
        onRowsPerPageChange={onRowsPerPageChangeHandler}
        dropdownProps={{
          onOpen: () => setIsFooterDropdownOpen(true),
          onClose: () => setIsFooterDropdownOpen(false),
          MenuProps: {
            autoFocus: false,
          },
        }}
      />
    ) : (
      <TableFooterShowMore
        data-testid="TableFooterShowMore"
        rowsPerPage={DEFAULT_ROWS_PER_PAGE}
        count={creditCardsCount}
        onRowsPerPageChange={onRowsPerPageChangeHandler}
      />
    );

  if (!paymentCards?.length) return null;

  return (
    <div data-testid="TableViewWrapper">
      <div className="sui-w-full sui-overflow-y-auto sui-no-scrollbar">
        <Table layout="fixed" density="normal">
          <TableHead>
            <TableRow>
              <TableCell
                padding="none"
                sticky={isSmallScreen ? 'horizontal' : undefined}
                width={CELL_WIDTH.CARD}
                sortDirection={cardNumberSortDirection}
              >
                <TableSortLabel
                  aria-label="Sort by card number"
                  active={currentSortByValue.sortBy === 'Card Number'}
                  direction={cardNumberSortDirection}
                  onClick={() => sortByField('Card Number')}
                  data-testid="sortBycardNumber"
                >
                  Card
                </TableSortLabel>
              </TableCell>
              <TableCell
                padding="none"
                width={CELL_WIDTH.NICKNAME}
                sortDirection={nicknameSortDirection}
              >
                <TableSortLabel
                  aria-label="Sort by card nickname"
                  active={currentSortByValue.sortBy === 'Nickname'}
                  direction={nicknameSortDirection}
                  onClick={() => sortByField('Nickname')}
                  data-testid="sortByNickname"
                >
                  Nickname
                </TableSortLabel>
              </TableCell>
              <TableCell
                padding="none"
                width={CELL_WIDTH.EXPIRATION}
                sortDirection={expDateSortDirection}
              >
                <TableSortLabel
                  aria-label="Sort by card expiration date"
                  active={currentSortByValue.sortBy === 'Expiration Date'}
                  direction={expDateSortDirection}
                  onClick={() => sortByField('Expiration Date')}
                  data-testid="sortByExpiry"
                >
                  Expiration
                </TableSortLabel>
              </TableCell>
              <TableCell
                padding="none"
                width={CELL_WIDTH.STATUS}
                sortDirection={statusSortDirection}
              >
                <TableSortLabel
                  aria-label="Sort by card status"
                  active={currentSortByValue.sortBy === 'Status'}
                  direction={statusSortDirection}
                  onClick={() => sortByField('Status')}
                  data-testid="sortByStatus"
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell padding="none" width={CELL_WIDTH.REMOTE_PURCHASES}>
                <div className="sui-flex sui-gap-0">
                  <TableRowLabel>Remote Purchases</TableRowLabel>
                  <InfoPopover iconSize="small" title="Remote Purchases">
                    <Typography variant="body-xs">
                      Let your team members check out at The Home Depot without
                      a physical credit card present. Approve and verify
                      in-store purchases over the phone (
                      <div className="sui-relative sui-inline-block sui-top-[4px]">
                        <Mobile size="small" />
                      </div>
                      ) or via text message ({' '}
                      <div className="sui-relative sui-inline-block sui-top-[4px] sui-left-[-2px]">
                        <TextToConfirm size="small" />
                      </div>
                      ).
                    </Typography>
                    <br />
                    <Typography variant="body-xs">
                      Adjust your settings in &quot;Actions.&quot;
                    </Typography>
                  </InfoPopover>
                </div>
              </TableCell>
              {canShowHDWallet && (
                <TableCell
                  padding="none"
                  width={CELL_WIDTH.HD_PASS}
                  data-testid="hd-pass-auth-column"
                >
                  <TableRowLabel>HD Pass</TableRowLabel>
                </TableCell>
              )}
              <TableCell
                padding="none"
                width={CELL_WIDTH.ADDED_BY}
                sortDirection={createdBySortDirection}
              >
                <TableSortLabel
                  aria-label="Sort by card added by"
                  active={currentSortByValue.sortBy === 'Added By'}
                  direction={createdBySortDirection}
                  onClick={() => sortByField('Added By')}
                  data-testid="sortByAddedBy"
                >
                  Added By
                </TableSortLabel>
              </TableCell>
              <TableCell
                padding="none"
                width={CELL_WIDTH.LAST_EDITED}
                sortDirection={lastEditedSortDirection}
              >
                <TableSortLabel
                  aria-label="Sort by card last edited date"
                  active={
                    currentSortByValue.sortBy === 'Last Edited Date' ||
                    !currentSortByValue.sortBy
                  }
                  direction={lastEditedSortDirection}
                  onClick={() => sortByField('Last Edited Date')}
                  data-testid="sortByLastEdited"
                >
                  Last Edited
                </TableSortLabel>
              </TableCell>
              <TableCell width={CELL_WIDTH.ACTIONS} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentCards?.map((card) => renderCardTableRow(card))}
          </TableBody>
        </Table>
      </div>
      {shouldShowPagination && (
        <div ref={tableFooterRef} className={paginationClasses}>
          {tablePagination}
        </div>
      )}
    </div>
  );
};
